import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Smartphone, Apple, ArrowLeft, KeyRound } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import { apiFetch, fetchAuthOptions } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";

// ---------------------------------------------------------------------------
// /get-app/android (ALL-5141)
//
// The one link every surface uses to hand out the signer app: the profile
// menu, the signer Get started page and the signer invite email (ALL-5138).
// It manages its own sign-in (a SELF_MANAGED_AUTH_PATH in App.tsx) so a
// signed-out visitor from the email is sent through Google and back here
// (return_to), rather than being dropped on /wallets. Signed in, it offers the
// existing authenticated download (SignerAppDownloadController; any signed-in
// human, no new permission) with the build's sha256 and version from
// latest.json, and install notes. There is no iOS build behind any link, so the
// iPhone card states that plainly and names a help path that exists, rather than
// promising a release channel it cannot serve (J7, ALL-5643).
// ---------------------------------------------------------------------------

export const GET_APP_PATH = "/get-app/android";
const APK_PATH = "/v1/downloads/signer-app/android/latest.apk";
const METADATA_PATH = "/v1/downloads/signer-app/android/latest.json";

/**
 * The sign-in start URL that brings the visitor back here. Only a configured
 * method whose start URL already carries return_to=/get-app/android is used;
 * the built-in default (return_to=/) would drop them on the home page, so it
 * falls back to the gateway login with the right return_to.
 */
export function signInUrlFor(methods: { id: string; enabled: boolean; startUrl: string }[]): string {
  const wanted = "return_to=" + encodeURIComponent(GET_APP_PATH);
  const method =
    methods.find((m) => m.enabled && m.id === "google-oidc" && m.startUrl.includes(wanted)) ??
    methods.find((m) => m.enabled && m.startUrl.includes(wanted));
  return method?.startUrl ?? "/auth/login?" + wanted;
}

interface AppMetadata {
  versionCode?: string;
  apkSha256?: string;
}

// A 404 (SIGNER_APP_NOT_PUBLISHED) is the backend's deliberate "never published" signal and is
// never retried. Any other non-2xx status or a thrown network error is treated as transient --
// dev.alloy.build is known to return a transient 401 on roughly 1 in 6 authenticated requests
// (page-delivery-resources.md gotcha 35, ALL-4754) -- and gets up to METADATA_RETRIES retries
// before falling back to a distinct, retryable "unavailable-transient" state.
const METADATA_RETRIES = 2;
const METADATA_RETRY_DELAYS_MS = [400, 900];

type MetadataState =
  | { kind: "loading" }
  | { kind: "ready"; metadata: AppMetadata }
  | { kind: "unavailable"; supportReference?: string }
  | { kind: "unavailable-transient"; supportReference?: string };

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Where "Back" goes. This page sits outside the app shell (it manages its own sign-in), so it has
 * no sidebar to return through: it gives its own way back (ALL-5502). Within the app, Back is the
 * previous page; opened directly (an email link, a new tab) there is no previous page, so it goes
 * Home. Signed-out visitors are sent on to sign in from Home.
 */
export const HOME_PATH = "/home";

export default function GetAppAndroidPage() {
  const t = useMobileSigningLocale().getApp;
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => (location.key !== "default" ? navigate(-1) : navigate(HOME_PATH));
  const step = useAuthStore((s) => s.step);
  const [signInUrl, setSignInUrl] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<MetadataState>({ kind: "loading" });
  // Bumped by the "Try again" button to re-run the metadata fetch effect.
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (step !== "login") return;
    let cancelled = false;
    fetchAuthOptions(GET_APP_PATH).then((methods) => {
      if (cancelled) return;
      setSignInUrl(signInUrlFor(methods));
    });
    return () => {
      cancelled = true;
    };
  }, [step]);

  useEffect(() => {
    if (step !== "app") return;
    let cancelled = false;

    async function loadWithRetry(): Promise<void> {
      for (let attempt = 0; ; attempt += 1) {
        let res: Response;
        let body: { supportReference?: string } = {};
        try {
          res = await apiFetch(METADATA_PATH, {}, "signer_app_metadata");
          body = await res.json().catch(() => ({}));
        } catch {
          // A thrown network error is transient, never "not published".
          if (cancelled) return;
          if (attempt < METADATA_RETRIES) {
            await delay(METADATA_RETRY_DELAYS_MS[attempt] ?? METADATA_RETRY_DELAYS_MS[METADATA_RETRY_DELAYS_MS.length - 1]);
            if (cancelled) return;
            continue;
          }
          setMetadata({ kind: "unavailable-transient" });
          return;
        }
        if (cancelled) return;

        if (res.ok) {
          setMetadata({ kind: "ready", metadata: body as AppMetadata });
          return;
        }

        const supportReference = body?.supportReference ?? res.headers.get("X-Alloy-Support-Reference") ?? undefined;

        // A real 404 (SIGNER_APP_NOT_PUBLISHED) is never retried -- it is the backend's
        // deliberate "this app was never published" signal, distinct from a transient failure.
        if (res.status === 404) {
          setMetadata({ kind: "unavailable", supportReference });
          return;
        }

        if (attempt < METADATA_RETRIES) {
          await delay(METADATA_RETRY_DELAYS_MS[attempt] ?? METADATA_RETRY_DELAYS_MS[METADATA_RETRY_DELAYS_MS.length - 1]);
          if (cancelled) return;
          continue;
        }

        setMetadata({ kind: "unavailable-transient", supportReference });
        return;
      }
    }

    setMetadata({ kind: "loading" });
    void loadWithRetry();
    return () => {
      cancelled = true;
    };
  }, [step, retryToken]);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-6" data-testid="get-app-android">
      <div>
        <Button variant="ghost" size="sm" onClick={goBack} className="-ms-2 mb-1" data-testid="get-app-back">
          <ArrowLeft size={16} aria-hidden="true" className="rtl:rotate-180" />
          {t.back}
        </Button>
        <Breadcrumb className="mb-2" items={[{ label: t.breadcrumbHome, href: HOME_PATH }, { label: t.title }]} />
        <h1 className="text-xl font-semibold text-ink">{t.title}</h1>
        <p className="mt-1 text-sm text-muted">{t.intro}</p>
      </div>

      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle className="flex items-center gap-2">
            <Smartphone size={18} aria-hidden="true" />
            {t.androidTitle}
          </CardTitle>
          {step === "loading" ? <div className="spinner" aria-label={t.checking} /> : null}
          {step === "login" ? (
            <>
              <p className="text-sm text-muted">{t.signInFirst}</p>
              <div>
                <Button
                  size="sm"
                  disabled={!signInUrl}
                  onClick={() => signInUrl && window.location.assign(signInUrl)}
                  data-testid="get-app-sign-in"
                >
                  {t.signIn}
                </Button>
              </div>
            </>
          ) : null}
          {step === "app" ? (
            <>
              {metadata.kind === "unavailable" ? (
                <div data-testid="get-app-unavailable">
                  <p className="text-sm text-muted">{t.unavailable}</p>
                  <SupportDetails reference={metadata.supportReference} />
                </div>
              ) : metadata.kind === "unavailable-transient" ? (
                <div data-testid="get-app-unavailable-transient">
                  <p className="text-sm text-muted">{t.unavailableTransient}</p>
                  <SupportDetails reference={metadata.supportReference} />
                  <div className="mt-2">
                    <Button size="sm" onClick={() => setRetryToken((n) => n + 1)} data-testid="get-app-retry">
                      {t.tryAgain}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <a
                      href={APK_PATH}
                      className={buttonVariants({ variant: "primary", size: "sm" })}
                      data-testid="get-app-download"
                    >
                      {t.download}
                    </a>
                  </div>
                  {metadata.kind === "ready" && metadata.metadata.apkSha256 ? (
                    <p className="text-xs text-muted">
                      {t.sha256Label}{" "}
                      <code dir="ltr" className="break-all font-mono" data-testid="get-app-sha256">
                        {metadata.metadata.apkSha256}
                      </code>
                      {metadata.metadata.versionCode ? (
                        <>
                          {" · "}
                          {t.versionLabel} <span data-testid="get-app-version">{metadata.metadata.versionCode}</span>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </>
              )}
              <div>
                <p className="text-sm font-semibold text-ink">{t.installTitle}</p>
                <ul className="mt-1 list-disc ps-5 text-sm text-muted">
                  <li>{t.installAllow}</li>
                  <li>{t.installSamsung}</li>
                  <li>{t.installThenPair}</li>
                </ul>
              </div>
            </>
          ) : null}
        </CardBody>
      </Card>

      {step === "app" ? (
        <Card>
          <CardBody className="flex flex-col gap-2">
            <CardTitle className="flex items-center gap-2">
              <KeyRound size={18} aria-hidden="true" />
              {t.pairNextTitle}
            </CardTitle>
            <p className="text-sm text-muted">{t.pairNextBody}</p>
            <div>
              <Link
                to="/signer/get-started"
                className={buttonVariants({ variant: "secondary", size: "sm" })}
                data-testid="get-app-pair-next"
              >
                {t.pairNextCta}
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle className="flex items-center gap-2">
            <Apple size={18} aria-hidden="true" />
            {t.iosTitle}
          </CardTitle>
          <p className="text-sm text-muted" data-testid="get-app-ios">
            {t.iosNotAvailable}
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
